import { useEffect, useRef, type FC } from 'react'
import { useForm, Controller } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { Input, Button, Alert, AlertType, GenericError, Checkbox, Link } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentHYCSLayout } from '@egw/layouts'
import { useRouter } from '@egw/router'
import { useGetEnrollmentHYCSGetStartedData, useSubmitEnrollmentHYCSGetStartedData } from '@egw/api'
import { configClient } from '@egw/config'
import { sendEvent } from '@egw/events'
import { WEALTH_ROUTES } from '@egw/constants'
import { PageHeader } from '../../../components'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { type Config } from '../../../config/config'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'
import { getZendeskAPIValue } from '../../../utils/zendesk/get-zendesk-api-value'
import { getTestId, HtmlTestTypes } from '../../../utils/testing/get-test-id'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'
import { ROUTES } from '../../../constants/routes/routes-generic'

const log = new Logger('Enrollment - HYCS - GetStarted')

interface FormData {
  email: string
}

export const GetStartedPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()

  const hasShownIntent = useRef(false)
  const hasAgreedToTerms = useRef(false)

  const config = configClient.getConfig() as Config

  const { data, isLoading, error } = useGetEnrollmentHYCSGetStartedData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentHYCSGetStartedData()

  const {
    control,
    handleSubmit,
    trigger,
    formState: { dirtyFields, errors: formErrors, isValid },
  } = useForm({
    defaultValues: {
      agreements: undefined,
      email: '',
    },
  })

  // On mount
  useEffect(() => {
    sendEvent(EDataLayerEventWealth.BANKHYCHYS_ENROLLMENT_PAGEVIEW)
  }, [])

  // On intent
  useEffect(() => {
    if (dirtyFields.email && !hasShownIntent.current) {
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_EMAIL_INTENT)
      hasShownIntent.current = true // Prevent further executions
    }
  }, [dirtyFields.email])

  // track when user agrees to terms
  useEffect(() => {
    if (isValid && !hasAgreedToTerms.current) {
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_TERMS_CONSENT)
      hasAgreedToTerms.current = true
    }
  }, [isValid])

  // Protect route
  useValidateRoute(data?.path)

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData
    if (isValid) {
      try {
        sendEvent(EDataLayerEventWealth.BANKHYCHYS_ENROLLMENT_STARTED)
        const response = await mutate({
          email: formData.email,
          privacyPolicy: {
            id: getZendeskAPIValue(config.zendeskArticles.privacyPolicy),
            accepted: true,
          },
          eSignConsent: {
            id: getZendeskAPIValue(config.zendeskArticles.eSignature),
            accepted: true,
          },
        })
        // If path exists and is different
        if (response?.path && newPathIsDifferent(location.pathname, response.path)) {
          navigate(response.path)
        } else {
          // If no path from BE, redirect to error
          if (!response?.path) {
            navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES.ERROR.path}`)
          }
        }
      } catch (e) {
        log.error('Error submitting', e)
      }
    }
  })

  return (
    <EnrollmentHYCSLayout title={t('hycs.get_started.page_title')}>
      <div className="egw-container-enroll-hycs h-full flex flex-col relative md:justify-center justify-start items-start">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error ? (
          <form
            onSubmit={(e) => void onSubmit(e)}
            className="w-full flex-1 flex flex-col justify-start lg:justify-center pb-5xl"
          >
            <PageHeader title={t('hycs.get_started.title')} />
            <p className="typo-primary-sm text-text-meta mb-lg" data-test-id="description_1">
              {t('hycs.get_started.description_1')}
            </p>
            <p className="typo-primary-sm text-text-meta mb-lg" data-test-id="description_2">
              {t('hycs.get_started.description_2')}
            </p>
            <Controller
              name="email"
              control={control}
              rules={{
                required: t('common.required_field'),
                pattern: {
                  value:
                    /^[a-zA-Z0-9](?:[a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/,
                  message: t('common.invalid_email'),
                },
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  onBlur={() => {
                    trigger('email')
                  }}
                  label={t('common.email')}
                  required
                  error={formErrors.email?.message}
                  autoComplete="email"
                  type="email"
                  className="mb-sm"
                  testId={getTestId('email', HtmlTestTypes.input)}
                />
              )}
            />

            <Controller
              name="agreements"
              control={control}
              rules={{
                required: t('common.required_field'),
              }}
              render={({ field }) => (
                <Checkbox
                  {...field}
                  required
                  type="checkbox"
                  className="mb-lg"
                  testId={getTestId('agreements', HtmlTestTypes.checkbox)}
                >
                  <span className="typo-primary-sm mt-[4.5px]">
                    I agree to the{' '}
                    <Link className="typo-primary-sm" href={config.zendeskArticles.privacyPolicy}>
                      Privacy Policy
                    </Link>{' '}
                    and{' '}
                    <Link className="typo-primary-sm" href={config.zendeskArticles.eSignature}>
                      Electronic Signature Consent
                    </Link>
                    .
                  </span>
                </Checkbox>
              )}
            />

            <p className="typo-primary-sm text-text-meta">{t('hycs.get_started.description_3')}</p>

            <p className="md:absolute relative bottom-2xl left-0 right-0 typo-primary-2xs-regular text-text-meta mt-3xl md:px-sm px-0">
              {t('hycs.get_started.footer_disclosure')}
            </p>

            <div className="flex-1 md:hidden" />

            {submitError ? <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" /> : null}

            {/* AUTO GENERATED COMPONENTS */}

            <Button
              type="submit"
              className="w-full md:w-min mt-xl"
              isDisabled={!isValid}
              isLoading={isSubmitting}
              testId="primaryAction_button"
            >
              {t('common.continue')}
            </Button>
          </form>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentHYCSLayout>
  )
}
